import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Download, Mail, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { VoucherDisplay } from '../components/voucher/VoucherDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

function base64ToBlob(b64, mime = 'application/pdf') {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export default function AdminVouchers() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [language, setLanguage] = useState('pt');
  const [sendEmail, setSendEmail] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vouchers/products`);
        if (!cancelled && Array.isArray(data.products)) {
          setProducts(data.products);
          if (data.products.length && !productId) {
            setProductId(data.products[0].id);
          }
        }
      } catch {
        // ignore — admin page is local
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === productId) || null,
    [products, productId]
  );

  const canSubmit =
    !!productId &&
    recipientName.trim().length >= 1 &&
    (!sendEmail || /\S+@\S+\.\S+/.test(buyerEmail));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/vouchers/issue`, {
        product_id: productId,
        recipient_name: recipientName.trim(),
        buyer_name: buyerName.trim(),
        buyer_email: buyerEmail.trim() || null,
        personal_message: personalMessage.trim(),
        language,
        send_email: sendEmail,
      });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to issue voucher.';
      setError(typeof detail === 'string' ? detail : 'Failed to issue voucher.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleDownload() {
    if (!result?.pdf_base64) return;
    const blob = base64ToBlob(result.pdf_base64);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.pdf_filename || 'voucher.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function handleReset() {
    setResult(null);
    setRecipientName('');
    setBuyerName('');
    setBuyerEmail('');
    setPersonalMessage('');
    setSendEmail(false);
    setError('');
  }

  return (
    <div className="pt-24 pb-20 bg-warm-50 min-h-screen">
      <div className="max-w-3xl mx-auto px-6">
        <div className="mb-8">
          <span className="text-accent font-medium text-xs tracking-wider uppercase mb-2 block">
            Admin
          </span>
          <h1 className="font-heading text-3xl md:text-4xl text-warm-900 mb-2">
            Issue voucher
          </h1>
          <p className="text-warm-600 text-sm">
            Generate a voucher PDF without going through Stripe. Optionally email it to the buyer.
          </p>
        </div>

        {!result && (
          <form
            onSubmit={handleSubmit}
            className="bg-white border border-warm-200 rounded-lg p-6 space-y-5 shadow-sm"
          >
            <div className="space-y-2">
              <Label htmlFor="product">Product *</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger id="product">
                  <SelectValue placeholder="Choose a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => {
                    const name = language === 'pt' ? p.name_pt : p.name_en;
                    return (
                      <SelectItem key={p.id} value={p.id}>
                        {name || p.id} — €{p.price_eur}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {selectedProduct && (
                <p className="text-xs text-warm-500">
                  {language === 'pt'
                    ? selectedProduct.details_pt
                    : selectedProduct.details_en}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="language">Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pt">Português</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipient">Recipient name *</Label>
              <Input
                id="recipient"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                maxLength={80}
                placeholder="Name shown on the voucher"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="buyer-name">Buyer name</Label>
                <Input
                  id="buyer-name"
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  maxLength={80}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="buyer-email">
                  Buyer email {sendEmail && '*'}
                </Label>
                <Input
                  id="buyer-email"
                  type="email"
                  value={buyerEmail}
                  onChange={(e) => setBuyerEmail(e.target.value)}
                  maxLength={120}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Personal message</Label>
              <Textarea
                id="message"
                value={personalMessage}
                onChange={(e) => setPersonalMessage(e.target.value)}
                maxLength={400}
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between border-t border-warm-200 pt-5">
              <div>
                <Label htmlFor="send-email" className="cursor-pointer">
                  Send by email
                </Label>
                <p className="text-xs text-warm-500 mt-1">
                  When off, the PDF is just downloaded.
                </p>
              </div>
              <Switch
                id="send-email"
                checked={sendEmail}
                onCheckedChange={setSendEmail}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 p-3 rounded">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={!canSubmit || submitting}
              className="w-full bg-accent hover:bg-accent-dark text-white rounded-full"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Issuing…
                </>
              ) : (
                'Issue voucher'
              )}
            </Button>
          </form>
        )}

        {result && (
          <div className="space-y-6">
            <div className="flex items-start gap-2 text-sm bg-green-50 border border-green-200 text-green-800 p-3 rounded">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div>
                  Voucher <b>{result.voucher.code}</b> issued.
                </div>
                {result.email_sent && (
                  <div className="flex items-center gap-1 mt-1">
                    <Mail className="w-3 h-3" /> Email sent to{' '}
                    {result.voucher.buyer_email}
                  </div>
                )}
              </div>
            </div>

            <VoucherDisplay voucher={result.voucher} />

            <div className="flex flex-wrap gap-3 justify-center">
              <Button
                onClick={handleDownload}
                className="bg-accent hover:bg-accent-dark text-white rounded-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button
                variant="outline"
                onClick={handleReset}
                className="rounded-full"
              >
                Issue another
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
